import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X, Clock, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function AvailabilitySummaryBar({ totalSent, totalConfirmed, totalDeclined, totalPending }) {
  if (totalSent === 0) return null;
  return (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground font-medium">{totalSent} sent</span>
      </div>
      {totalConfirmed > 0 && (
        <div className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400" /><span className="text-xs text-emerald-400 font-semibold">{totalConfirmed} available</span></div>
      )}
      {totalDeclined > 0 && (
        <div className="flex items-center gap-1"><X className="w-3.5 h-3.5 text-red-400" /><span className="text-xs text-red-400 font-semibold">{totalDeclined} unavailable</span></div>
      )}
      {totalPending > 0 && (
        <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-amber-400" /><span className="text-xs text-amber-400 font-semibold">{totalPending} pending</span></div>
      )}
    </div>
  );
}


export function AvailabilityEventCard({ ev, isExpanded, onToggle }) {
  const navigate = useNavigate();
  const hasChecks = ev.checks_sent > 0;
  const unchecked = ev.crew_assigned - ev.checks_sent;

  return (
    <div className="rounded-sm border border-border bg-secondary/10 overflow-hidden">
      <button
        data-testid={`avail-widget-event-${ev.event_id}`}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-secondary/20 transition-colors text-left"
        onClick={onToggle}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{ev.event_name}</p>
          <p className="text-xs text-muted-foreground">{ev.venue} · {ev.start_date}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-3">
          {hasChecks ? (
            <>
              {ev.confirmed > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-emerald-500/10 text-emerald-400">{ev.confirmed}</Badge>}
              {ev.declined > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-red-500/10 text-red-400">{ev.declined}</Badge>}
              {ev.pending > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-amber-500/10 text-amber-400">{ev.pending}</Badge>}
            </>
          ) : (
            <span className="text-xs text-muted-foreground/50">{ev.crew_assigned} crew — not sent</span>
          )}
          <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {isExpanded && hasChecks && (
        <div className="px-3 pb-2.5 space-y-1 border-t border-border pt-2">
          {ev.checks.map(c => {
            const isYes = c.status === "yes";
            const isNo = c.status === "no";
            const isPending = c.status === "pending";
            return (
              <div key={c.check_id} className={`flex items-center justify-between px-2 py-1.5 rounded-sm ${isYes ? 'bg-emerald-500/10' : isNo ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
                <div className="flex items-center gap-2">
                  {isYes && <Check className="w-3 h-3 text-emerald-400" />}
                  {isNo && <X className="w-3 h-3 text-red-400" />}
                  {isPending && <Clock className="w-3 h-3 text-amber-400" />}
                  <span className="text-xs font-medium">{c.crew_name}</span>
                </div>
                <span className={`text-xs font-semibold ${isYes ? 'text-emerald-400' : isNo ? 'text-red-400' : 'text-amber-400'}`}>
                  {isYes ? 'Available' : isNo ? 'Unavailable' : 'Pending'}
                </span>
              </div>
            );
          })}
          {unchecked > 0 && <p className="text-xs text-muted-foreground/50 px-2 pt-1">{unchecked} crew not yet checked</p>}
          <Button data-testid={`avail-widget-goto-${ev.event_id}`} variant="ghost" size="sm" className="h-6 text-xs text-primary w-full mt-1"
            onClick={() => navigate('/events', { state: { highlightEvent: ev.event_id } })}>
            View Event <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}

      {isExpanded && !hasChecks && (
        <div className="px-3 pb-2.5 border-t border-border pt-2">
          <p className="text-xs text-muted-foreground/60">No availability checks sent yet.</p>
          <Button data-testid={`avail-widget-goto-${ev.event_id}`} variant="ghost" size="sm" className="h-6 text-xs text-primary mt-1"
            onClick={() => navigate('/events', { state: { highlightEvent: ev.event_id } })}>
            Send from Event Page <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
